<template>
  <Modal v-if="location" :invert="true" layout="full">
    <div class="container">
      <div class="info">
        <LocationList
          :locations="[locationWithMeta]"
          :hover="true"
          :params="{
            images: true,
            clickable: false,
            highlight: false
          }"
        >
          <div class="list">
            <ProductionList
              :productions="productions"
              :params="{ highlight: false, images: true, link: true }"
            />
          </div>
        </LocationList>
        <SvgIcon icon="brut-outlined" :unwrap="false" />
      </div>
      <div class="media">
        <MediaCollection
          v-if="images.length"
          :images="images"
          v-slot="{ image }"
        >
          <template v-if="image.production">
            <ProductionImageCaption :production="image.production" />
          </template>
          <template v-else-if="image.location">
            {{ image.location.title }}
          </template>
          <template v-if="image.caption">{{ image.caption }}</template>
        </MediaCollection>
      </div>
    </div>
    <template v-slot:nav>
      <LocationOverlayNavigation :location="location" />
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex'

import { socialUrl } from '@/util/helpers'
import LocationList from '@/components/LocationList'
import LocationOverlayNavigation from '@/components/LocationOverlayNavigation'
import MediaCollection from '@/components/MediaCollection'
import Modal from '@/components/Modal'
import ProductionImageCaption from '@/components/ProductionImageCaption'
import ProductionList from '@/components/ProductionList'
import SvgIcon from '@/components/SvgIcon'

export default {
  name: 'LocationOverlay',
  props: ['slug'],
  components: {
    LocationList,
    LocationOverlayNavigation,
    MediaCollection,
    Modal,
    ProductionImageCaption,
    ProductionList,
    SvgIcon
  },
  computed: {
    ...mapGetters(['allLocations', 'locations', 'allProductions']),
    location() {
      return this.findLocation(this.slug)
    },
    locationWithMeta() {
      return {
        ...this.location,
        productionCount: this.productions.length
      }
    },
    locationId() {
      return this.location ? this.location._id : null
    },
    indexInLocations() {
      return this.locations.indexOf(this.location)
    },
    prevLocation() {
      return this.locations[this.indexInLocations - 1]
    },
    nextLocation() {
      return this.locations[this.indexInLocations + 1]
    },
    productions() {
      return this.allProductions.filter((p) => p.locationId === this.locationId)
    },
    images() {
      return this.locationImages.concat(this.productionImages)
    },
    locationImages() {
      const images = this.location.images || []
      const imagesWithMeta = images.map((img) => {
        img.location = this.location
        return img
      })
      return imagesWithMeta
    },
    productionImages() {
      return this.productions.reduce((all, production) => {
        const images = production.images || []
        const imagesWithMeta = images.map((img) => {
          img.production = production
          return img
        })
        return all.concat(imagesWithMeta || [])
      }, [])
    }
  },
  methods: {
    findLocation(slugOrId) {
      return this.allLocations.find((l) => [l._id, l.slug].includes(slugOrId))
    }
  },
  metaInfo() {
    const title = this.location?.title || ''
    const image = socialUrl(this.location?.images?.[0])

    return {
      title,
      meta: [
        { vmid: 'og:title', name: 'og:title', content: title },
        { vmid: 'og:image', name: 'og:image', content: image },
        { vmid: 'twitter:title', name: 'twitter:title', content: title },
        { vmid: 'twitter:image', name: 'twitter:image', content: image }
      ]
    }
  }
}
</script>

<style scoped>
.container {
  --container-top-padding: calc(3.5 * var(--page-padding));
  --header-height: 0;
  display: flex;
  flex-direction: column;
  padding: var(--page-padding);
  padding-top: var(--container-top-padding);
  --color-background: var(--color-background-inverted);
  --color-highlight: var(--color-highlight-inverted);
  @mixin breakpoint-small {
    --container-top-padding: calc(3.15 * var(--page-padding));
  }
  @mixin breakpoint-medium {
    --container-top-padding: calc(2.6 * var(--page-padding));
  }
  @mixin breakpoint-large {
    --container-top-padding: calc(2.5 * var(--page-padding));
  }
}
.info {
  width: 100%;
  display: flex;
  flex-direction: column;
  color: var(--color-text);
}
/deep/ {
  .info {
    .icon-brut-outlined {
      @mixin brut-list-icon;
      display: none;
    }
    .label {
      --color-highlight: var(--color-text);
    }
  }
}
.media {
  width: 100%;
  color: var(--color-text-inverted);
  padding-top: 4em;
  padding-bottom: 1em;
}
@mixin breakpoint-medium {
  .container {
    flex-direction: row;
    padding: 0;
    height: 100vh;
  }
  .info {
    width: calc(var(--content-width-aside) + var(--page-padding));
    padding: var(--page-padding);
    padding-top: var(--container-top-padding);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
  }
  .media {
    width: calc(var(--content-width-main) + var(--page-padding));
    padding: var(--page-padding);
    padding-top: var(--container-top-padding);
    padding-left: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  /deep/ {
    .info {
      .icon-brut-outlined {
        display: block;
      }
    }
  }
}
</style>
